import apiClient from './config';
import { Plaza } from '../../types/index';
import type { ResultadoPrecio } from '../../types/suscripciones';

// Interfaz para los parámetros de búsqueda
export interface BusquedaPlazaParams {
    dia: string;
    horaInicio: string;
    nivel_id: string;
}

export interface ProgressResponse {
    _id: string;
    total: number;
    processed: number;
    status: 'processing' | 'completed' | 'error';
    type: string;
    createdAt: string;
    processingStats?: {
        totalProcesados: number;
        totalActualizados: number;
    };
    error?: string;
}

// Interfaz para los datos de actualización del estado de espera
export interface ActualizarEstadoEsperaParams {
    clienteId: string;
    enEspera: boolean;
    fechaInicioEspera?: Date | null;
    fechaFinEspera?: Date | null;
    motivoEspera?: string | null;
    updatedBy?: string;
}

// Interfaz para la respuesta de actualización de estado de espera
export interface ActualizarEstadoEsperaResponse {
    message: string;
    cliente: {
        _id: string;
        nombre: string;
        enEspera: boolean;
        fechaInicioEspera?: Date | null;
        fechaFinEspera?: Date | null;
        motivoEspera?: string | null;
        // Otros campos del cliente...
    };
    plazasActualizadas: number;
    /**
     * P4 — cuántas clases se han liberado al pausar. Una plaza en pausa ya no
     * guarda el sitio: se saca al cliente de las clases del periodo y el hueco
     * queda disponible (y corre su lista de espera).
     */
    plazasLiberadas?: number;
    /**
     * P4 — solo al reactivar: a qué clases ha vuelto a entrar y en cuáles no
     * cupo porque se llenaron mientras estaba fuera. Por esas se le ha dado una
     * recuperación, igual que al dar una plaza.
     */
    reactivacion?: {
        total: number;
        eventosActualizados: number;
        clasesCompletas: ClaseCompletaResultado[];
    } | null;
}

// Respuesta de la eliminación de plaza
interface EliminarPlazaResponse {
    message: string;
    plaza: Plaza;
    eventosActualizados: number;
    totalEventos: number;
    // El back recalcula el importe tras quitar la plaza.
    precio?: ResultadoPrecio;
}

// Clase de una plaza en la que el cliente no cupo, y si se le compensó.
export interface ClaseCompletaResultado {
    eventoId: string;
    fecha: string;
    horaInicio: string;
    compensada?: boolean;
}

// Respuesta de la creación de plaza
interface CrearPlazaResponse {
    message: string;
    plaza: Plaza;
    eventosActualizados: number;
    totalEventos: number;
    clasesCompletas?: ClaseCompletaResultado[];
    precio?: ResultadoPrecio;
}

// ============================================
// Choques: clases ya generadas que estarían llenas para unas plazas nuevas
// ============================================

export interface ClaseCompleta {
    eventoId: string;
    fecha: string;
    horaInicio: string;
    inicio: string;
    aforo: number;
    // Quién ocupa sitio sin tener plaza en ese horario.
    motivo: { recuperaciones: number; pruebas: number; otros: number };
    // Posterior a la primera clase real del cliente → se le cobra → sí.
    compensarPorDefecto: boolean;
}

export interface ChoquesPlaza {
    dia: string;
    horaInicio: string;
    nivel_id: string;
    primeraClase: string | null;
    clasesConSitio: number;
    clasesCompletas: ClaseCompleta[];
}

export interface ChoquesResponse {
    primeraClaseCliente: string | null;
    plazas: ChoquesPlaza[];
}

export const fetchChoquesPlazas = async (params: {
    plazas: BusquedaPlazaParams[];
    clienteId?: string;
    prospectId?: string;
}): Promise<ChoquesResponse> => {
    const response = await apiClient.post('/plazas/choques', params);
    return response.data;
};

// Obtener todas las plazas
export const fetchPlazas = async (): Promise<Plaza[]> => {
    const response = await apiClient.get('/plazas');
    return response.data;
};

// Obtener plazas activas
export const fetchActivePlazas = async (): Promise<Plaza[]> => {
    const response = await apiClient.get('/plazas/active');
    return response.data;
};

// Obtener plaza por ID
export const fetchPlazaById = async (id: string): Promise<Plaza> => {
    const response = await apiClient.get(`/plazas/${id}`);
    return response.data;
};

// Crear plaza
export const createPlaza = async (plaza: Omit<Plaza, '_id'>): Promise<Plaza> => {
    const response = await apiClient.post('/plazas', plaza);
    return response.data;
};

// Actualizar plaza
export const updatePlaza = async (plaza: Plaza): Promise<Plaza> => {
    const response = await apiClient.put(`/plazas/${plaza._id}`, plaza);
    return response.data;
};

// Desactivar plaza (borrado lógico)
export const softDeletePlaza = async (id: string, updatedBy: string): Promise<Plaza> => {
    const response = await apiClient.patch(`/plazas/${id}/soft-delete`, { updatedBy });
    return response.data;
};

// Eliminar plaza permanentemente
export const deletePlaza = async (id: string): Promise<void> => {
    await apiClient.delete(`/plazas/${id}`);
};

// Verificar disponibilidad de plazas
export const verificarDisponibilidadPlazas = async (params: BusquedaPlazaParams[]) => {
    const response = await apiClient.post('/plazas/verificar-disponibilidad', params);
    return response.data;
};

// Buscar plazas por cliente_id
export const fetchPlazasByClientId = async (cliente_id: string): Promise<Plaza[]> => {
    const response = await apiClient.get(`/plazas/cliente/${cliente_id}`);
    return response.data;
};

// Para crear una nueva plaza con actualización de eventos.
// `suscripcionId` es opcional: el backend valida que haya precio para las plazas
// resultantes antes de crear nada y después recalcula el importe (`precio` en la
// respuesta). `tarifaId`: la tarifa elegida si el nivel tiene varias.
// `compensaciones`: por cada clase llena (id), si se le da una recuperación.
export const crearPlazaConEventos = async (
    plaza: Plaza & {
        suscripcionId?: string;
        tarifaId?: string;
        compensaciones?: Record<string, boolean>;
        // Clases llenas que se vieron en el aviso (ver recomprobación al guardar).
        choquesVistos?: string[];
    }
): Promise<CrearPlazaResponse> => {
    const respuesta = await apiClient.post('/plazas/con-eventos', plaza);
    return respuesta.data; // Incluye sessionId para seguimiento
};

// Eliminar plaza y actualizar eventos futuros (con proceso en batches)
export const eliminarPlazaYActualizarEventos = async (params: {
    plazaId: string;
    clientId: string;
    updatedBy?: string;
}): Promise<EliminarPlazaResponse> => {
    const { plazaId, clientId, updatedBy } = params;
    const response = await apiClient.delete(`/plazas/${plazaId}/cliente/${clientId}`, {
        data: { updatedBy }
    });
    return response.data;
};

// Mover/editar la plaza de un cliente a un nuevo slot (día/hora/nivel) — M5.
// Re-mapea al cliente: lo saca de los eventos futuros del slot antiguo y lo añade
// a los del nuevo. No cambia el nº de plazas, pero sí puede cambiar el precio si
// el nivel nuevo tiene otra tarifa: el back lo recalcula (`precio`).
export const moverPlaza = async (params: {
    plazaId: string;
    dia: string;
    horaInicio: string;
    nivel_id: string;
    updatedBy?: string;
    // Tarifa elegida si el nivel nuevo tiene varias.
    tarifaId?: string;
}): Promise<{
    message: string;
    plaza: Plaza;
    eventosRemovidos: number;
    eventosAgregados: number;
    clasesCompletas?: ClaseCompletaResultado[];
    precio?: ResultadoPrecio;
}> => {
    const { plazaId, dia, horaInicio, nivel_id, updatedBy, tarifaId } = params;
    const response = await apiClient.put(`/plazas/${plazaId}/mover`, { dia, horaInicio, nivel_id, updatedBy, tarifaId });
    return response.data;
};

// Actualizar estado de espera de un cliente y sus plazas
export const actualizarEstadoEspera = async (params: ActualizarEstadoEsperaParams): Promise<ActualizarEstadoEsperaResponse> => {
    const { clienteId, ...data } = params;
    const response = await apiClient.patch(`/plazas/cliente/${clienteId}/espera`, data);
    return response.data;
};

// ============================================
// NUEVO: Disponibilidad de horarios por nivel
// ============================================

export interface DisponibilidadHorario {
    dia: string;
    horaInicio: string;
    nivel_id: string;
    ocupadas: number;
    disponibles: number;
    estado: 'disponible' | 'completo';
}

export interface DisponibilidadNivelResponse {
    horarios: DisponibilidadHorario[];
}

/**
 * Obtiene disponibilidad de horarios para un nivel específico
 * @param nivelId - ID del nivel
 * @param startDate - Fecha inicio en formato ISO (ya ajustada desde frontend)
 * @param endDate - Fecha fin en formato ISO (ya ajustada desde frontend)
 */
export const fetchDisponibilidadNivel = async (
    nivelId: string,
    startDate: string,
    endDate: string
): Promise<DisponibilidadNivelResponse> => {
    const response = await apiClient.get('/plazas/disponibilidad-nivel', {
        params: { nivelId, startDate, endDate }
    });
    return response.data;
};
